import React, { useMemo, useState } from "react";
import Modal from "./Modal";
import { btn } from "../utils/buttonStyles";
import { useApi } from "../hooks/useApi";
import {
  ASSET_CLASSES, CRITERIA, IMPACTS, SITE_TYPES, apiError, cyberAssetProblems, userLabel,
} from "../utils/nercAssets";

/**
 * GRC → Assets → NERC sites: the add / edit / retire forms. Each form checks the CIP-002 rules
 * it can before sending (utils/nercAssets) and always shows the server's message when it refuses.
 * A child (ESP, system, Cyber Asset) belongs to one site for life: there is no site field on edit.
 */

export const inputCls = "w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none";
const labelCls = "block text-xs font-medium text-gray-700 mb-1";

export function Field({ label, children, hint }) {
  return (
    <label className="block">
      <span className={labelCls}>{label}</span>
      {children}
      {hint && <span className="block text-[11px] text-gray-500 mt-1">{hint}</span>}
    </label>
  );
}

export function Errors({ items }) {
  if (!items?.length) return null;
  return (
    <ul className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 space-y-1" role="alert">
      {items.map((e) => <li key={e}>{e}</li>)}
    </ul>
  );
}

export function Actions({ saving, onCancel, label = "Save" }) {
  return (
    <div className="flex justify-end gap-2 pt-2">
      <button type="button" onClick={onCancel} className={btn("neutral", "md")}>Cancel</button>
      <button type="submit" disabled={saving} className={btn("primary", "md")}>
        {saving ? "Saving…" : label}
      </button>
    </div>
  );
}

/** Runs the request; on failure keeps the form open with the server's messages. */
export function useSubmit(onSaved) {
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState([]);
  const submit = async (request, localErrors = []) => {
    if (localErrors.length) { setErrors(localErrors); return; }
    setSaving(true); setErrors([]);
    try {
      await request();
      onSaved();
    } catch (err) {
      const list = err?.response?.data?.errors;
      setErrors(Array.isArray(list) && list.length ? list : [apiError(err, "Could not save. Try again.")]);
    } finally {
      setSaving(false);
    }
  };
  return { saving, errors, submit };
}

export function UserSelect({ users, value, onChange, label, testId }) {
  return (
    <Field label={label}>
      <select className={inputCls} value={value ?? ""} onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)} data-testid={testId}>
        <option value="">— None —</option>
        {(users || []).map((u) => <option key={u.id} value={u.id}>{userLabel(u)}</option>)}
      </select>
    </Field>
  );
}

// ── Site ─────────────────────────────────────────────────────────────────────────────────────

export function SiteForm({ site, users, onClose, onSaved }) {
  const apiCall = useApi();
  const [form, setForm] = useState({
    name: site?.name || "", siteType: site?.siteType || "Substation",
    isCip014CriticalFacility: !!site?.isCip014CriticalFacility, location: site?.location || "",
    ownerUserId: site?.ownerUserId ?? null, notes: site?.notes || "",
  });
  const { saving, errors, submit } = useSubmit(onSaved);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = (e) => {
    e.preventDefault();
    submit(() => site
      ? apiCall("PUT", `/nerc-assets/sites/${site.id}`, form)
      : apiCall("POST", "/nerc-assets/sites", form),
    form.name.trim() ? [] : ["Site name is required."]);
  };

  return (
    <Modal isOpen onClose={onClose} title={site ? "Edit site" : "Add site"} size="lg">
      <form onSubmit={onSubmit} className="space-y-3" data-testid="nerc-site-form">
        <Field label="Name"><input className={inputCls} value={form.name} onChange={(e) => set("name")(e.target.value)} maxLength={200} /></Field>
        <Field label="Site type">
          <select className={inputCls} value={form.siteType} onChange={(e) => set("siteType")(e.target.value)}>
            {SITE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </Field>
        <label className="flex items-start gap-2 text-sm">
          <input type="checkbox" className="mt-1" checked={form.isCip014CriticalFacility} onChange={(e) => set("isCip014CriticalFacility")(e.target.checked)} />
          <span>CIP-014 critical facility<span className="block text-[11px] text-gray-500">Identified under CIP-014 R1 and verified under R2.</span></span>
        </label>
        <Field label="Location"><input className={inputCls} value={form.location} onChange={(e) => set("location")(e.target.value)} maxLength={400} /></Field>
        <UserSelect users={users} value={form.ownerUserId} onChange={set("ownerUserId")} label="Owner" />
        <Field label="Notes"><textarea className={inputCls} rows={2} value={form.notes} onChange={(e) => set("notes")(e.target.value)} maxLength={2000} /></Field>
        <p className="text-xs text-gray-500">The site's impact rating is not entered: it is the highest impact of its BES Cyber Systems.</p>
        <Errors items={errors} />
        <Actions saving={saving} onCancel={onClose} />
      </form>
    </Modal>
  );
}

// ── ESP ──────────────────────────────────────────────────────────────────────────────────────

export function EspForm({ siteId, esp, onClose, onSaved }) {
  const apiCall = useApi();
  const [name, setName] = useState(esp?.name || "");
  const [description, setDescription] = useState(esp?.description || "");
  const { saving, errors, submit } = useSubmit(onSaved);
  const onSubmit = (e) => {
    e.preventDefault();
    const body = { siteId, name, description };
    submit(() => esp ? apiCall("PUT", `/nerc-assets/esps/${esp.id}`, body) : apiCall("POST", "/nerc-assets/esps", body),
      name.trim() ? [] : ["Name is required."]);
  };
  return (
    <Modal isOpen onClose={onClose} title={esp ? "Edit Electronic Security Perimeter" : "Add Electronic Security Perimeter"} size="lg">
      <form onSubmit={onSubmit} className="space-y-3" data-testid="nerc-esp-form">
        <Field label="Name"><input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} maxLength={200} /></Field>
        <Field label="Description"><textarea className={inputCls} rows={2} value={description} onChange={(e) => setDescription(e.target.value)} maxLength={2000} /></Field>
        <Errors items={errors} />
        <Actions saving={saving} onCancel={onClose} />
      </form>
    </Modal>
  );
}

// ── BES Cyber System ─────────────────────────────────────────────────────────────────────────

export function SystemForm({ siteId, system, users, onClose, onSaved }) {
  const apiCall = useApi();
  const [form, setForm] = useState({
    name: system?.name || "", impact: system?.impact || "Medium", criterion: system?.criterion || "",
    description: system?.description || "", ownerUserId: system?.ownerUserId ?? null,
  });
  const { saving, errors, submit } = useSubmit(onSaved);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const criteria = CRITERIA[form.impact] || [];

  const onSubmit = (e) => {
    e.preventDefault();
    const local = [];
    if (!form.name.trim()) local.push("Name is required.");
    if (!criteria.includes(form.criterion)) local.push(`Choose the Attachment 1 criterion (${form.impact} impact).`);
    const body = { siteId, ...form };
    submit(() => system ? apiCall("PUT", `/nerc-assets/systems/${system.id}`, body) : apiCall("POST", "/nerc-assets/systems", body), local);
  };

  return (
    <Modal isOpen onClose={onClose} title={system ? "Edit BES Cyber System" : "Add BES Cyber System"} size="lg">
      <form onSubmit={onSubmit} className="space-y-3" data-testid="nerc-system-form">
        <Field label="Name"><input className={inputCls} value={form.name} onChange={(e) => set("name")(e.target.value)} maxLength={200} /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Impact rating">
            <select className={inputCls} value={form.impact} onChange={(e) => setForm((f) => ({ ...f, impact: e.target.value, criterion: "" }))} data-testid="nerc-system-impact">
              {IMPACTS.map((i) => <option key={i} value={i}>{i}</option>)}
            </select>
          </Field>
          <Field label="CIP-002 Attachment 1 criterion" hint="High 1.1–1.4 · Medium 2.1–2.13 · Low 3.1–3.6">
            <select className={inputCls} value={form.criterion} onChange={(e) => set("criterion")(e.target.value)} data-testid="nerc-system-criterion">
              <option value="">Choose…</option>
              {criteria.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
        </div>
        <UserSelect users={users} value={form.ownerUserId} onChange={set("ownerUserId")} label="Owner" />
        <Field label="Description"><textarea className={inputCls} rows={2} value={form.description} onChange={(e) => set("description")(e.target.value)} maxLength={2000} /></Field>
        <Errors items={errors} />
        <Actions saving={saving} onCancel={onClose} />
      </form>
    </Modal>
  );
}

// ── Cyber Asset ──────────────────────────────────────────────────────────────────────────────

export function CyberAssetForm({ siteId, asset, systems, esps, onClose, onSaved }) {
  const apiCall = useApi();
  const [form, setForm] = useState({
    name: asset?.name || "", classification: asset?.classification || "BCA", espId: asset?.espId ?? null,
    systemIds: asset?.systemIds || [], hostname: asset?.hostname || "", ipAddress: asset?.ipAddress || "",
    manufacturer: asset?.manufacturer || "", model: asset?.model || "", firmwareOrOs: asset?.firmwareOrOs || "",
    routableConnectivity: !!asset?.routableConnectivity, interactiveRemoteAccess: !!asset?.interactiveRemoteAccess,
  });
  const { saving, errors, submit } = useSubmit(onSaved);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const liveSystems = useMemo(() => (systems || []).filter((s) => !s.retiredAt), [systems]);
  const liveEsps = useMemo(() => (esps || []).filter((e) => !e.retiredAt), [esps]);
  const systemsById = useMemo(() => Object.fromEntries(liveSystems.map((s) => [s.id, s])), [liveSystems]);
  const toggleSystem = (id) => setForm((f) => ({
    ...f, systemIds: f.systemIds.includes(id) ? f.systemIds.filter((x) => x !== id) : [...f.systemIds, id],
  }));

  const onSubmit = (e) => {
    e.preventDefault();
    const local = form.name.trim() ? [] : ["Name is required."];
    local.push(...cyberAssetProblems(form, systemsById));
    const body = { siteId, ...form };
    submit(() => asset ? apiCall("PUT", `/nerc-assets/cyber-assets/${asset.id}`, body) : apiCall("POST", "/nerc-assets/cyber-assets", body), local);
  };

  return (
    <Modal isOpen onClose={onClose} title={asset ? "Edit Cyber Asset" : "Add Cyber Asset"} size="2xl">
      <form onSubmit={onSubmit} className="space-y-3" data-testid="nerc-asset-form">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Name"><input className={inputCls} value={form.name} onChange={(e) => set("name")(e.target.value)} maxLength={200} /></Field>
          <Field label="Classification">
            <select className={inputCls} value={form.classification} onChange={(e) => set("classification")(e.target.value)} data-testid="nerc-asset-class">
              {ASSET_CLASSES.map((c) => <option key={c.value} value={c.value} title={c.title}>{c.label} — {c.title}</option>)}
            </select>
          </Field>
        </div>
        <fieldset>
          <legend className={labelCls}>BES Cyber Systems (one or more)</legend>
          {liveSystems.length === 0
            ? <p className="text-sm text-gray-500">Add a BES Cyber System to this site first.</p>
            : (
              <div className="flex flex-wrap gap-2">
                {liveSystems.map((s) => (
                  <label key={s.id} className={`flex items-center gap-1.5 text-sm px-2 py-1 rounded-lg border ${form.systemIds.includes(s.id) ? "border-indigo-400 bg-indigo-50" : "border-gray-200"}`}>
                    <input type="checkbox" checked={form.systemIds.includes(s.id)} onChange={() => toggleSystem(s.id)} />
                    {s.name} <span className="text-[11px] text-gray-500">{s.impact}</span>
                  </label>
                ))}
              </div>
            )}
        </fieldset>
        <Field label="Electronic Security Perimeter">
          <select className={inputCls} value={form.espId ?? ""} onChange={(e) => set("espId")(e.target.value ? Number(e.target.value) : null)}>
            <option value="">— Not inside an ESP —</option>
            {liveEsps.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
          </select>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Hostname"><input className={inputCls} value={form.hostname} onChange={(e) => set("hostname")(e.target.value)} maxLength={256} /></Field>
          <Field label="IP address"><input className={inputCls} value={form.ipAddress} onChange={(e) => set("ipAddress")(e.target.value)} maxLength={64} /></Field>
          <Field label="Manufacturer"><input className={inputCls} value={form.manufacturer} onChange={(e) => set("manufacturer")(e.target.value)} maxLength={200} /></Field>
          <Field label="Model"><input className={inputCls} value={form.model} onChange={(e) => set("model")(e.target.value)} maxLength={200} /></Field>
          <Field label="Firmware / OS"><input className={inputCls} value={form.firmwareOrOs} onChange={(e) => set("firmwareOrOs")(e.target.value)} maxLength={200} /></Field>
        </div>
        <div className="flex flex-wrap gap-4 text-sm">
          <label className="flex items-center gap-2"><input type="checkbox" checked={form.routableConnectivity} onChange={(e) => set("routableConnectivity")(e.target.checked)} /> Routable connectivity</label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={form.interactiveRemoteAccess} onChange={(e) => set("interactiveRemoteAccess")(e.target.checked)} /> Interactive Remote Access</label>
        </div>
        <Errors items={errors} />
        <Actions saving={saving} onCancel={onClose} />
      </form>
    </Modal>
  );
}

// ── Retire ───────────────────────────────────────────────────────────────────────────────────

const RETIRE_PATHS = { site: "sites", esp: "esps", system: "systems", asset: "cyber-assets" };

/** Retiring keeps the row (and its name) in the history; nothing is deleted. A reason is required. */
/** endpoint: the retire URL when the item is not an inventory row (e.g. a patch source). */
export function RetireForm({ kind, item, endpoint, onClose, onSaved }) {
  const apiCall = useApi();
  const [reason, setReason] = useState("");
  const { saving, errors, submit } = useSubmit(onSaved);
  const onSubmit = (e) => {
    e.preventDefault();
    submit(() => apiCall("POST", endpoint || `/nerc-assets/${RETIRE_PATHS[kind]}/${item.id}/retire`, { reason }),
      reason.trim() ? [] : ["A reason is required."]);
  };
  return (
    <Modal isOpen onClose={onClose} title={`Retire "${item.name}"`} size="lg">
      <form onSubmit={onSubmit} className="space-y-3" data-testid="nerc-retire-form">
        <p className="text-sm text-gray-600">
          Retired items stay in the history and keep their names, and can no longer be changed.
        </p>
        <Field label="Reason"><textarea className={inputCls} rows={2} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} /></Field>
        <Errors items={errors} />
        <Actions saving={saving} onCancel={onClose} label="Retire" />
      </form>
    </Modal>
  );
}
